import { useMemo } from 'react';
import { useHasActiveSSSAccount } from 'src/components/Navigation/hooks/useHasActiveSSSAccount';
import {
	NavigationCategory,
	NavigationOptionKey,
	MobileCategoryOption,
	MobileNavigationOption,
	DesktopCategoryOption,
	DesktopNavigationOption,
} from 'src/components/Navigation/types';
import {
	getMobileNavigationOptions,
	getDesktopNavigationOptions,
} from 'src/components/Navigation/config/consts';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

type ScreenSize = 'mobile' | 'desktop';

type Params<T extends ScreenSize = ScreenSize> = { screenSize: T };

type UseNavItems<T> = [
	NavigationCategory,
	T extends 'mobile' ? MobileNavigationOption : DesktopNavigationOption,
][];

export default function useNavItems<T extends ScreenSize>({
	screenSize,
}: Params<T>): UseNavItems<T> {
	const hasActiveSSSAccount = useHasActiveSSSAccount();
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const navOptionsToUse = useMemo(() => {
		return screenSize === 'mobile'
			? getMobileNavigationOptions({ isPrivyNativeAuth })
			: getDesktopNavigationOptions({ isPrivyNativeAuth });
	}, [screenSize, isPrivyNativeAuth]);

	return useMemo((): UseNavItems<T> => {
		const navOptionEntries = Object.entries(navOptionsToUse) as UseNavItems<T>;

		return navOptionEntries.map(([navigationCategory, navOption]) => {
			const categoryName = navOption.categoryName;

			// Filter out the super stake sol option if the authority does not have an active SSS account.
			if (categoryName === 'earn' && navOption.type === 'categoryOption') {
				if (hasActiveSSSAccount) {
					return [navigationCategory, navOption];
				}

				const updatedNavOption = getOptionsFilteredByKey(
					navOption,
					'superstakesol'
				);

				return [navigationCategory, updatedNavOption];
			}

			// Filter out the bet option if the authority does not have an open bet position.
			if (categoryName === 'more' && navOption.type === 'categoryOption') {
				const updatedNavOption = getOptionsFilteredByKey(navOption, 'bet');

				return [navigationCategory, updatedNavOption];
			}

			return [navigationCategory, navOption];
		});
	}, [hasActiveSSSAccount, navOptionsToUse]);
}

function getOptionsFilteredByKey<
	T extends MobileCategoryOption | DesktopCategoryOption,
>(option: T, key: NavigationOptionKey) {
	const filteredOptions = option.options.filter(
		(categoryOption) => categoryOption.key !== key
	);

	return {
		...option,
		options: filteredOptions,
	};
}
